import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DollarSign, Search, Edit, User, ArrowLeft, Briefcase, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
const getHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getFromLocalStorage('accessToken')}` });
const METHOD_LABELS = { nakit: 'Nakit', havale_eft: 'Havale/EFT', kredi_karti: 'Kredi Karti', toptancidan: 'Toptancidan' };
const TYPE_LABELS = { normal: 'Normal', taksit: 'Taksit', soz: 'Soz' };

const PaymentsTab = ({ can, customers = [] }) => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerHistory, setCustomerHistory] = useState(null);
  const [editPayment, setEditPayment] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editMethod, setEditMethod] = useState('');
  const [editNote, setEditNote] = useState('');

  const loadPayments = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/payments/all?`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (selectedCustomerId) url += `customer_id=${selectedCustomerId}&`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const d = await res.json();
        setPayments(d.payments || []);
        setTotal(d.total || 0);
      }
    } catch (e) { console.error('Load payments error:', e); }
  }, [startDate, endDate, search, selectedCustomerId]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const loadCustomerHistory = async (customerId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/customer/${customerId}/history`, { headers: getHeaders() });
      if (res.ok) {
        const d = await res.json();
        setCustomerHistory(d);
      }
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleEdit = async () => {
    if (!editPayment) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/edit`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({
          jobId: editPayment.jobId,
          paymentId: editPayment.id,
          amount: editAmount ? parseFloat(editAmount) : undefined,
          method: editMethod || undefined,
          note: editNote || undefined,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast({ title: "Odeme Guncellendi" });
      setEditPayment(null);
      loadPayments();
      if (customerHistory) loadCustomerHistory(customerHistory.customer?.id);
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const openEdit = (p) => {
    setEditPayment(p);
    setEditAmount(p.amount?.toString());
    setEditMethod(p.method || 'nakit');
    setEditNote(p.note || '');
  };

  const byMethod = {};
  payments.forEach(p => { const m = p.method || 'nakit'; byMethod[m] = (byMethod[m] || 0) + (p.amount || 0); });

  // Unique customers from payments for quick filter
  const uniqueCustomers = {};
  payments.forEach(p => {
    if (p.customerId && p.customerName) uniqueCustomers[p.customerId] = p.customerName;
  });
  // Also add from props
  (customers || []).forEach(c => {
    if (c.id && c.name) uniqueCustomers[c.id] = c.name;
  });

  const STATUS_MAP = {
    beklemede: 'Beklemede', randevu_planlandi: 'Randevu', arac_kabul: 'Arac Kabul',
    islemde: 'Islemde', kalite_kontrol: 'Kalite', teslime_hazir: 'Teslime Hazir', teslim_edildi: 'Teslim Edildi',
  };

  return (
    <div className="space-y-4" data-testid="payments-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Card className="bg-gray-800/70 border-gray-700/40 col-span-2 md:col-span-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-green-400" data-testid="total-collected">{total.toLocaleString()} TL</p>
          <p className="text-gray-400 text-xs">Toplam Tahsilat</p>
        </CardContent></Card>
        {Object.entries(byMethod).map(([m, amt]) => (
          <Card key={m} className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-2 text-center">
            <p className="text-lg font-bold text-white">{amt.toLocaleString()} TL</p>
            <p className="text-gray-400 text-[10px]">{METHOD_LABELS[m] || m}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-end">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Musteri, plaka veya not..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white pl-10 h-8 text-sm" data-testid="payment-search" />
        </div>
        <Select value={selectedCustomerId || 'all'} onValueChange={v => setSelectedCustomerId(v === 'all' ? '' : v)}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm w-48" data-testid="customer-filter-select">
            <SelectValue placeholder="Tum Musteriler" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700 max-h-60">
            <SelectItem value="all" className="text-white hover:bg-gray-800">Tum Musteriler</SelectItem>
            {Object.entries(uniqueCustomers).map(([id, name]) => (
              <SelectItem key={id} value={id} className="text-white hover:bg-gray-800">{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white h-8 text-sm w-36" />
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white h-8 text-sm w-36" />
        <Button size="sm" onClick={loadPayments} className="bg-brand hover:bg-brand text-white h-8" data-testid="filter-payments-btn">
          Filtrele
        </Button>
      </div>

      {/* Customer Detail Button */}
      {selectedCustomerId && (
        <Button size="sm" onClick={() => loadCustomerHistory(selectedCustomerId)}
          className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs" data-testid="view-customer-history-btn">
          <User className="h-3 w-3 mr-1" />Musteri Odeme Gecmisi
        </Button>
      )}

      {/* Payment List */}
      <div className="space-y-1.5 max-h-[500px] overflow-y-auto" data-testid="payment-list">
        {payments.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8" data-testid="no-payments">Odeme bulunamadi</p>
        ) : payments.map((p, idx) => (
          <Card key={p.id || idx} className="bg-gray-800/60 border-gray-700/40" data-testid={`payment-row-${p.id || idx}`}>
            <CardContent className="p-2">
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm cursor-pointer hover:text-brand"
                      onClick={() => p.customerId && loadCustomerHistory(p.customerId)}
                      data-testid={`payment-customer-link-${p.id || idx}`}>{p.customerName}</span>
                    <span className="text-brand text-xs">{p.carPlate}</span>
                    <Badge className="bg-gray-700 text-white text-[10px]">{METHOD_LABELS[p.method] || p.method}</Badge>
                    {p.paymentType && p.paymentType !== 'normal' && (
                      <Badge className="bg-blue-700 text-white text-[10px]">{TYPE_LABELS[p.paymentType] || p.paymentType}</Badge>
                    )}
                    {p.jobStatus && (
                      <Badge className="bg-gray-600 text-white text-[10px]">{STATUS_MAP[p.jobStatus] || p.jobStatus}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                    <span>{new Date(p.date).toLocaleString('tr-TR')}</span>
                    {p.note && <span className="text-gray-500">"{p.note}"</span>}
                    <span>Giren: {p.enteredByName || p.enteredBy || '-'}</span>
                    {p.editedBy && <span className="text-yellow-500">Duzenlendi: {p.editedBy}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400 font-bold text-lg">{p.amount?.toLocaleString()} TL</span>
                  {can('can_edit_payments') && (
                    <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 h-7"
                      onClick={() => openEdit(p)} data-testid={`edit-payment-${p.id || idx}`}>
                      <Edit className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Customer History Modal */}
      <Dialog open={!!customerHistory} onOpenChange={() => setCustomerHistory(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <User className="h-5 w-5 text-brand" />
              Musteri Odeme Gecmisi - {customerHistory?.customer?.name || ''}
            </DialogTitle>
          </DialogHeader>
          {customerHistory && (
            <div className="space-y-4" data-testid="customer-history-modal">
              {/* Customer Summary */}
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-400 text-xs">Toplam Tutar</p>
                  <p className="text-white font-bold text-lg">{customerHistory.totalPrice?.toLocaleString()} TL</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-400 text-xs">Odenen</p>
                  <p className="text-green-400 font-bold text-lg">{customerHistory.totalPaid?.toLocaleString()} TL</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-400 text-xs">Kalan</p>
                  <p className={`font-bold text-lg ${customerHistory.totalRemaining > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {customerHistory.totalRemaining > 0 ? customerHistory.totalRemaining.toLocaleString() + ' TL' : 'Tamam'}
                  </p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-400 text-xs">Odeme Sayisi</p>
                  <p className="text-blue-400 font-bold text-lg">{customerHistory.paymentCount}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/40">
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold text-sm">{customerHistory.customer?.name}</p>
                  {customerHistory.customer?.phone && <span className="text-gray-400 text-xs">{customerHistory.customer.phone}</span>}
                  {customerHistory.customer?.email && <span className="text-gray-400 text-xs">{customerHistory.customer.email}</span>}
                </div>
              </div>

              {/* Jobs Summary */}
              {customerHistory.jobs?.length > 0 && (
                <div>
                  <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-brand" />Is Kayitlari ({customerHistory.jobs.length})
                  </h4>
                  {customerHistory.jobs.map(j => (
                    <div key={j.jobId} className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-2 mb-1.5">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-brand text-xs font-bold">{j.carPlate}</span>
                          <Badge className="bg-gray-600 text-white text-[10px]">{STATUS_MAP[j.status] || j.status}</Badge>
                          <span className="text-gray-400 text-[10px]">{(j.services || []).join(', ')}</span>
                        </div>
                        <div className="text-right text-xs">
                          <span className="text-white font-bold">{j.totalPrice?.toLocaleString()} TL</span>
                          <span className="text-gray-500 mx-1">|</span>
                          <span className="text-green-400">{j.paidAmount?.toLocaleString()}</span>
                          {j.remainingBalance > 0 && <span className="text-red-400 ml-1">-{j.remainingBalance?.toLocaleString()}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Full Payment History */}
              <div>
                <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-400" />Tum Odemeler ({customerHistory.paymentCount})
                </h4>
                {customerHistory.payments?.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">Odeme kaydi yok</p>
                ) : customerHistory.payments?.map((p, i) => (
                  <div key={p.id || i} className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-2 mb-1.5" data-testid={`hist-payment-${p.id || i}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-gray-700 text-white text-[10px]">{METHOD_LABELS[p.method] || p.method}</Badge>
                          {p.paymentType && p.paymentType !== 'normal' && (
                            <Badge className="bg-blue-700 text-white text-[10px]">{TYPE_LABELS[p.paymentType] || p.paymentType}</Badge>
                          )}
                          <span className="text-brand text-[10px]">{p.carPlate}</span>
                          <span className="text-gray-500 text-[10px]">{(p.services || []).join(', ')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
                          <span>{new Date(p.date).toLocaleString('tr-TR')}</span>
                          {p.note && <span>"{p.note}"</span>}
                          <span>Giren: {p.enteredByName || p.enteredBy || '-'}</span>
                          {p.editedBy && <span className="text-yellow-500">Duzenlendi: {p.editedBy}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-400 font-bold">{p.amount?.toLocaleString()} TL</span>
                        {can('can_edit_payments') && (
                          <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 h-6"
                            onClick={() => { openEdit(p); setCustomerHistory(null); }}
                            data-testid={`hist-edit-${p.id || i}`}>
                            <Edit className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={!!editPayment} onOpenChange={() => setEditPayment(null)}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader><DialogTitle className="text-white">Odeme Duzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="bg-gray-800 rounded-lg p-2 text-xs text-gray-400">
              <p>Musteri: <span className="text-white">{editPayment?.customerName}</span></p>
              <p>Plaka: <span className="text-brand">{editPayment?.carPlate}</span></p>
              <p>Tarih: <span className="text-white">{editPayment ? new Date(editPayment.date).toLocaleString('tr-TR') : ''}</span></p>
            </div>
            <Input type="number" value={editAmount} onChange={e => setEditAmount(e.target.value)}
              placeholder="Tutar" className="bg-gray-800 border-gray-700 text-white" data-testid="edit-payment-amount" />
            <Select value={editMethod} onValueChange={setEditMethod}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="nakit" className="text-white">Nakit</SelectItem>
                <SelectItem value="havale_eft" className="text-white">Havale/EFT</SelectItem>
                <SelectItem value="kredi_karti" className="text-white">Kredi Karti</SelectItem>
                <SelectItem value="toptancidan" className="text-white">Toptancidan</SelectItem>
              </SelectContent>
            </Select>
            <Input value={editNote} onChange={e => setEditNote(e.target.value)}
              placeholder="Not" className="bg-gray-800 border-gray-700 text-white" data-testid="edit-payment-note" />
            <Button onClick={handleEdit} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="save-edit-payment-btn">
              <DollarSign className="h-4 w-4 mr-1" />Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsTab;
